import { axios_adapter } from '../network/axios_adapter';
import ENDPOINTS from '../network/endpoints';

const HOW_IT_WORKS_STEPS = [
  'Invite your friends and family to register on Rapchik.',
  'When your friends and family register on the app, both of you will get 1000 RapCoins.',
  'RapCoins can be used to let you get entry in a game and in play next contest .',
];

function pickString(item, keys, fallback = '') {
  for (const key of keys) {
    const value = item?.[key];
    if (value != null && value !== '') return String(value).trim();
  }
  return fallback;
}

function pickNumber(item, keys) {
  for (const key of keys) {
    const value = item?.[key];
    if (value != null && value !== '') {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return null;
}

function formatReferralCode(code = '') {
  const trimmed = String(code ?? '').trim();
  if (!trimmed) return '';
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

function formatReferredCount(value) {
  if (value == null || value === '') return '00';
  const count = Number(value);
  if (!Number.isFinite(count)) return String(value);
  return String(count).padStart(2, '0');
}

function unwrapStepsList(rawSteps) {
  if (Array.isArray(rawSteps)) return rawSteps;
  if (rawSteps && typeof rawSteps === 'object') {
    return rawSteps.steps ?? rawSteps.items ?? rawSteps.list ?? null;
  }
  return null;
}

export function resolveReferralStepText(step) {
  if (typeof step === 'string') return step.trim();
  if (!step || typeof step !== 'object') return '';

  if (typeof step.text === 'string') return step.text.trim();
  if (typeof step.description === 'string') return step.description.trim();
  if (typeof step.step === 'string') return step.step.trim();
  if (typeof step.content === 'string') return step.content.trim();
  if (typeof step.heading === 'string') return step.heading.trim();

  if (typeof step.title === 'string') return step.title.trim();
  if (step.title && typeof step.title === 'object') {
    return resolveReferralStepText(step.title);
  }

  return pickString(step, ['text', 'description', 'title', 'step', 'content', 'heading', 'label']);
}

function normalizeHowItWorksSteps(rawSteps) {
  const list = unwrapStepsList(rawSteps);
  if (!Array.isArray(list) || !list.length) return [...HOW_IT_WORKS_STEPS];

  const steps = list.map((step) => resolveReferralStepText(step)).filter(Boolean);

  return steps.length ? steps : [...HOW_IT_WORKS_STEPS];
}

function mapReferralData(item = {}) {
  const referralCode = formatReferralCode(
    pickString(item, ['referralCode', 'code', 'inviteCode', 'myReferralCode']),
  );
  const plainCode = referralCode.replace(/^#/, '');
  const shareUrl =
    pickString(item, ['shareUrl', 'referralLink', 'inviteLink', 'shareLink'], '') ||
    (plainCode ? `${window.location.origin}/?ref=${encodeURIComponent(plainCode)}` : '');

  return {
    totalRapCoinsEarned:
      pickNumber(item, [
        'totalRapCoinsEarned',
        'rapCoinsEarned',
        'coinsEarned',
        'totalEarned',
        'earnedRapCoins',
        'totalCoinsEarned',
      ]) ?? 0,
    totalReferred:
      pickNumber(item, [
        'totalReferred',
        'referredCount',
        'referralCount',
        'totalReferrals',
        'referredUsersCount',
      ]) ?? 0,
    referralCode,
    shareUrl,
    shareMessage:
      pickString(
        item,
        ['shareMessage', 'message', 'shareText'],
        referralCode
          ? `Join me on Rapchik! Use my referral code ${referralCode} and earn rewards.`
          : 'Join me on Rapchik!',
      ),
    howItWorksSteps: normalizeHowItWorksSteps(
      item?.howItWorksSteps ??
        item?.howItWorks ??
        item?.howDoesItWork ??
        item?.how_it_works ??
        item?.steps,
    ),
  };
}

function extractReferralRecord(body) {
  const data = body?.data ?? body;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};

  const nested =
    (data.referral && typeof data.referral === 'object' && data.referral) ||
    (data.myReferrals && typeof data.myReferrals === 'object' && data.myReferrals) ||
    (data.myReferral && typeof data.myReferral === 'object' && data.myReferral) ||
    null;

  if (nested) {
    return {
      ...data,
      ...nested,
      howItWorksSteps:
        nested.howItWorksSteps ??
        nested.howItWorks ??
        data.howItWorksSteps ??
        data.howItWorks,
    };
  }

  return data;
}

export async function fetchReferralData() {
  const response = await axios_adapter.get(ENDPOINTS.USER_REFERRALS);
  const body = response.data;

  if (body?.responseCode && body.responseCode !== 200) {
    throw new Error(body?.data?.message || body?.message || 'Unable to load referrals.');
  }

  return mapReferralData(extractReferralRecord(body));
}

export function getFormattedReferredCount(totalReferred) {
  return formatReferredCount(totalReferred);
}

export async function copyReferralCode(referralCode) {
  const plainCode = String(referralCode || '').replace(/^#/, '');
  if (!plainCode) throw new Error('Referral code is not available.');
  await navigator.clipboard.writeText(plainCode);
  return plainCode;
}

export async function shareReferralLink(data) {
  const shareData = {
    title: 'Join Rapchik',
    text: data?.shareMessage || 'Join me on Rapchik!',
    url: data?.shareUrl,
  };

  if (navigator.share) {
    await navigator.share(shareData);
    return true;
  }

  if (data?.shareUrl) {
    await navigator.clipboard.writeText(data.shareUrl);
    return false;
  }

  throw new Error('Unable to share referral link.');
}
